/**
 * CART PAGE - Hidden Variant Automatic Switcher
 * Automatically switches cart line item variants based on quantity changes
 * Works with: Cart page, AJAX cart, Cart drawer
 * Compatible with all Shopify themes
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  /**
   * Define your variant rules here
   * Must match the rules in product-variant-switcher.js
   * 
   * Format: { productId: { minQty: variantId } }
   */
  const VARIANT_RULES = window.variantRules || {};
  const VARIANT_RULES_ADVANCED = window.variantRulesAdvanced || {};

  /**
   * Debounce delay for quantity changes (ms)
   */
  const DEBOUNCE_DELAY = 300;

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Get the correct variant ID based on quantity (simple mode)
   */
  function getVariantIdForQuantity(productId, quantity) {
    const rules = VARIANT_RULES[productId];
    if (!rules) return null;

    const thresholds = Object.keys(rules)
      .map(Number)
      .sort((a, b) => b - a);

    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return rules[threshold];
      }
    }

    return null;
  }

  /**
   * Get the correct variant ID based on option value and quantity (advanced mode)
   */
  function getVariantIdForQuantityAdvanced(productId, optionValue, quantity) {
    const rules = VARIANT_RULES_ADVANCED[productId];
    if (!rules) return null;

    const optionRules = rules[optionValue];
    if (!optionRules) return null;

    const thresholds = Object.keys(optionRules)
      .map(Number)
      .sort((a, b) => b - a);

    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return optionRules[threshold];
      }
    }

    return null;
  }

  /**
   * Get product ID from variant ID using Shopify API
   */
  async function getProductIdFromVariant(variantId) {
    try {
      const response = await fetch(`/products.json?limit=250`);
      const data = await response.json();
      
      for (let product of data.products) {
        const variant = product.variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          return product.id.toString();
        }
      }
    } catch (error) {
      console.error('[Cart Variant Switcher] Error fetching product data:', error);
    }
    return null;
  }

  /**
   * Debounce function to prevent too many API calls
   */
  function debounce(func, wait) {
    let timeout;
    const debouncedFunction = function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
    
    // Add cancel method
    debouncedFunction.cancel = function() {
      clearTimeout(timeout);
    };
    
    return debouncedFunction;
  }

  // ============================================
  // CART API FUNCTIONS
  // ============================================

  /**
   * Get current cart state
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('[Cart Variant Switcher] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Update cart line item with new variant
   */
  async function updateCartLineItem(lineKey, newVariantId, quantity, properties = {}) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        throw new Error('Cart update failed');
      }

      const cart = await response.json();
      
      // Check if variant actually changed
      const updatedItem = cart.items.find(item => item.key === lineKey);
      if (updatedItem && updatedItem.variant_id.toString() !== newVariantId.toString()) {
        // Variant didn't change, need to remove and re-add
        return await replaceCartLineItem(lineKey, newVariantId, quantity, properties);
      }

      return cart;
    } catch (error) {
      console.error('[Cart Variant Switcher] Error updating cart:', error);
      return null;
    }
  }

  /**
   * Replace cart line item by removing old and adding new variant
   * Used when /cart/change.js doesn't switch the variant
   */
  async function replaceCartLineItem(lineKey, newVariantId, quantity, properties = {}) {
    try {
      // Step 1: Remove old line item
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      // Step 2: Add new variant
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: newVariantId,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add new variant');
      }

      return await response.json();
    } catch (error) {
      console.error('[Cart Variant Switcher] Error replacing line item:', error);
      return null;
    }
  }

  /**
   * Refresh cart UI
   */
  async function refreshCartUI() {
    console.log('[Cart Variant Switcher] Refreshing cart UI...');
    
    // Simple page reload for cart page (most reliable)
    if (window.location.pathname.includes('/cart')) {
      console.log('[Cart Variant Switcher] Reloading cart page...');
      window.location.reload();
      return;
    }
    
    // For cart drawer - fetch and update only the items, not the whole drawer
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
    if (cartDrawer) {
      console.log('[Cart Variant Switcher] Refreshing cart drawer items...');
      
      // Get section ID from cart drawer
      const sectionId = cartDrawer.dataset.sectionId || 
                       cartDrawer.closest('[data-section-id]')?.dataset.sectionId;
      
      if (sectionId) {
        try {
          // Fetch fresh cart drawer HTML
          const response = await fetch(`/?section_id=${sectionId}`);
          const html = await response.text();
          
          // Parse the HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find cart items container in both old and new HTML
          const oldCartItems = cartDrawer.querySelector('cart-items, .cart-items, [data-cart-items]');
          const newCartItems = doc.querySelector('cart-items, .cart-items, [data-cart-items]');
          
          if (oldCartItems && newCartItems) {
            // Replace only the items, not the whole drawer
            oldCartItems.innerHTML = newCartItems.innerHTML;
            console.log('[Cart Variant Switcher] Cart items updated successfully');
          } else {
            // Fallback: update cart drawer footer (totals)
            const oldFooter = cartDrawer.querySelector('.cart-drawer__footer, [data-cart-footer]');
            const newFooter = doc.querySelector('.cart-drawer__footer, [data-cart-footer]');
            if (oldFooter && newFooter) {
              oldFooter.innerHTML = newFooter.innerHTML;
            }
            console.log('[Cart Variant Switcher] Cart drawer footer updated');
          }
          
          // Re-initialize listeners after refresh
          setTimeout(() => {
            initializeCartListeners();
          }, 100);
        } catch (error) {
          console.error('[Cart Variant Switcher] Error refreshing cart drawer:', error);
        }
      }
    }
    
    // Trigger update events
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    // For themes using Shopify section rendering
    if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cart) {
      if (typeof Shopify.theme.cart.getCart === 'function') {
        Shopify.theme.cart.getCart();
      }
    }
  }

  // ============================================
  // MAIN LOGIC
  // ============================================

  /**
   * Process a single cart line item
   */
  async function processCartLineItem(item) {
    const currentVariantId = item.variant_id.toString();
    const quantity = item.quantity;
    const productId = item.product_id.toString();
    let correctVariantId = null;

    // Check if product uses advanced rules (with size/option)
    if (VARIANT_RULES_ADVANCED[productId]) {
      // Get the first option value from variant (e.g., size)
      const optionValue = item.options_with_values && item.options_with_values[0] 
        ? item.options_with_values[0].value 
        : (item.variant_title ? item.variant_title.split(' / ')[0] : null);
      
      if (optionValue) {
        correctVariantId = getVariantIdForQuantityAdvanced(productId, optionValue, quantity);
        console.log(`[Cart Variant Switcher] Advanced mode - Size: ${optionValue}, Quantity: ${quantity}`);
      }
    }
    // Otherwise use simple rules
    else if (VARIANT_RULES[productId]) {
      correctVariantId = getVariantIdForQuantity(productId, quantity);
    }
    else {
      return null; // No rules for this product
    }
    
    if (!correctVariantId) {
      return null;
    }

    // Check if variant needs to be switched
    if (currentVariantId !== correctVariantId.toString()) {
      console.log(`[Cart Variant Switcher] Switching line item from variant ${currentVariantId} to ${correctVariantId} for quantity ${quantity}`);
      
      // Preserve properties
      const properties = item.properties || {};
      
      // Update the cart
      const result = await replaceCartLineItem(item.key, correctVariantId, quantity, properties);
      return result;
    }

    return null;
  }

  /**
   * Process all cart items
   */
  async function processAllCartItems() {
    const cart = await getCart();
    if (!cart || !cart.items) {
      return;
    }

    let hasChanges = false;

    for (let item of cart.items) {
      const result = await processCartLineItem(item);
      if (result) {
        hasChanges = true;
      }
    }

    if (hasChanges) {
      refreshCartUI();
    }
  }

  /**
   * Enforce minimum quantity on cart input
   */
  function enforceMinimumQuantity(input) {
    const minQuantity = parseInt(input.getAttribute('min')) || 
                       parseInt(input.getAttribute('data-min')) || 1;
    const currentValue = parseInt(input.value) || 1;
    
    if (currentValue < minQuantity) {
      console.log(`[Cart Variant Switcher] Enforcing minimum quantity: ${currentValue} -> ${minQuantity}`);
      input.value = minQuantity;
      return true;
    }
    return false;
  }

  /**
   * Handle quantity input change
   */
  const handleQuantityChange = debounce(async function(event) {
    const input = event.target;
    
    // Enforce minimum quantity first
    enforceMinimumQuantity(input);
    
    // Get line item key from input - try multiple methods
    let lineKey = input.dataset.index || 
                  input.dataset.key ||
                  input.getAttribute('data-index') ||
                  input.getAttribute('data-key');
    
    // If still not found, try to extract from input ID
    if (!lineKey && input.id) {
      // ID format: "Quantity-section-id-KEY"
      const idParts = input.id.split('-');
      if (idParts.length >= 3) {
        lineKey = idParts.slice(2).join('-'); // Get everything after "Quantity-section"
      }
    }
    
    // Try to find from closest cart item
    if (!lineKey) {
      const cartItem = input.closest('tr.cart-item, [data-cart-item], .cart-item');
      if (cartItem && cartItem.id) {
        // ID format: "CartItem-KEY"
        lineKey = cartItem.id.replace('CartItem-', '');
      }
    }
    
    if (!lineKey) {
      console.warn('[Cart Variant Switcher] Could not find line item key from input');
      return;
    }
    
    console.log('[Cart Variant Switcher] Processing line item:', lineKey);

    // Get current cart to find this item
    const cart = await getCart();
    if (!cart) return;

    const item = cart.items.find(i => i.key === lineKey || i.id.toString() === lineKey);
    if (!item) {
      console.warn('[Cart Variant Switcher] Could not find item in cart');
      return;
    }

    // Process this item and wait for completion
    const result = await processCartLineItem(item);
    
    if (result) {
      console.log('[Cart Variant Switcher] Item processed, refreshing UI...');
      await refreshCartUI();
    } else {
      console.log('[Cart Variant Switcher] No changes needed');
    }
  }, DEBOUNCE_DELAY);

  // ============================================
  // EVENT LISTENERS
  // ============================================

  // Track initialized inputs to prevent duplicate listeners
  const initializedInputs = new WeakSet();
  
  /**
   * Initialize cart quantity listeners
   */
  function initializeCartListeners() {
    // Run on cart page OR when cart drawer exists
    const isCartPage = window.location.pathname.includes('/cart');
    const isCheckoutPage = window.location.pathname.includes('/checkout');
    const hasCartDrawer = document.querySelector('.cart-drawer, #CartDrawer, cart-drawer') !== null;
    
    if (isCheckoutPage) {
      console.log('[Cart Variant Switcher] On checkout page, skipping initialization');
      return;
    }
    
    if (!isCartPage && !hasCartDrawer) {
      console.log('[Cart Variant Switcher] Not on cart page and no cart drawer found, skipping initialization');
      return;
    }
    
    console.log('[Cart Variant Switcher] Initializing...', { isCartPage, hasCartDrawer });
    
    // Find all quantity inputs in cart (NOT product page)
    const quantitySelectors = [
      'cart-items input[name="updates[]"]', // Only in cart-items
      '.cart__items input[name="updates[]"]', // Only in cart items
      '.cart-drawer input[name="updates[]"]', // Only in cart drawer
      'input.cart-item__quantity',
      '.cart__quantity input'
    ];

    let inputCount = 0;
    quantitySelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(input => {
        // Skip if already initialized
        if (initializedInputs.has(input)) {
          console.log('[Cart Variant Switcher] Input already initialized, skipping:', input.id);
          return;
        }
        
        // Mark as initialized
        initializedInputs.add(input);
        
        // Remove old listeners (just in case)
        input.removeEventListener('change', handleQuantityChange);
        input.removeEventListener('blur', handleQuantityChange);
        input.removeEventListener('input', handleQuantityChange);
        
        // Add new listeners
        input.addEventListener('change', handleQuantityChange);
        input.addEventListener('input', handleQuantityChange); // Also listen to input for immediate feedback
        
        // Blur event - process immediately (like Enter key)
        input.addEventListener('blur', async (e) => {
          const input = e.target;
          const lineKey = input.dataset.index || input.dataset.key || input.getAttribute('data-index');
          
          if (!lineKey) return;
          
          // Get quantity from input value
          const newQuantity = parseInt(input.value) || 1;
          const oldQuantity = parseInt(input.dataset.cartQuantity) || 0;
          
          // Only process if quantity actually changed
          if (newQuantity === oldQuantity) {
            console.log('[Cart Variant Switcher] Blur: Quantity unchanged, skipping');
            return;
          }
          
          console.log('[Cart Variant Switcher] Blur: Processing quantity change from', oldQuantity, 'to', newQuantity);
          
          // Cancel any pending debounced call
          if (handleQuantityChange.cancel) {
            handleQuantityChange.cancel();
          }
          
          const cart = await getCart();
          if (!cart) return;
          
          const item = cart.items.find(i => i.key === lineKey || i.id.toString() === lineKey);
          if (!item) return;
          
          // Override item quantity with user's input
          item.quantity = newQuantity;
          
          const result = await processCartLineItem(item);
          if (result) {
            await refreshCartUI();
          }
        });
        
        // Listen for Enter key - use keydown instead of keypress
        const enterHandler = (e) => {
          console.log('[Cart Variant Switcher] Key pressed:', e.key, 'keyCode:', e.keyCode);
          if (e.key === 'Enter' || e.keyCode === 13) {
            console.log('[Cart Variant Switcher] ✓ Enter key detected, processing immediately...');
            e.preventDefault();
            e.stopPropagation();
            
            // Cancel any pending debounced call
            if (handleQuantityChange.cancel) {
              handleQuantityChange.cancel();
            }
            
            // Process immediately without debounce
            const processImmediately = async () => {
              const input = e.target;
              const lineKey = input.dataset.index || input.dataset.key || input.getAttribute('data-index');
              
              if (!lineKey) {
                console.warn('[Cart Variant Switcher] No line key found');
                return;
              }
              
              // Get quantity from input value (user's input), not from cart API
              const newQuantity = parseInt(input.value) || 1;
              console.log('[Cart Variant Switcher] Processing line item immediately:', lineKey, 'with quantity:', newQuantity);
              
              const cart = await getCart();
              if (!cart) return;
              
              const item = cart.items.find(i => i.key === lineKey || i.id.toString() === lineKey);
              if (!item) return;
              
              // Override item quantity with user's input
              item.quantity = newQuantity;
              
              const result = await processCartLineItem(item);
              if (result) {
                await refreshCartUI();
              }
            };
            
            processImmediately();
          }
        };
        
        // Remove old listeners
        input.removeEventListener('keydown', enterHandler, true);
        input.removeEventListener('keydown', enterHandler, false);
        
        // Add listener with capture phase (runs before other listeners)
        input.addEventListener('keydown', enterHandler, true);
        console.log('[Cart Variant Switcher] Added Enter listener (capture phase) to input:', input.id);
        
        inputCount++;
      });
    });

    // Handle increment/decrement buttons
    const quantityButtons = document.querySelectorAll(
      '.cart-item__quantity-button, .quantity__button, [data-quantity-button], button[name="plus"], button[name="minus"]'
    );
    
    quantityButtons.forEach(button => {
      button.removeEventListener('click', handleButtonClick);
      button.addEventListener('click', handleButtonClick);
    });

    console.log(`[Cart Variant Switcher] Listeners initialized - Found ${inputCount} quantity inputs and ${quantityButtons.length} buttons`);
  }

  /**
   * Handle quantity button clicks
   */
  function handleButtonClick(event) {
    const button = event.currentTarget;
    
    // Try multiple ways to find the quantity input
    let quantityInput = button.closest('tr.cart-item')?.querySelector('input[type="number"]') ||
                       button.closest('.cart-item')?.querySelector('input[type="number"]') ||
                       button.closest('[data-cart-item]')?.querySelector('input[type="number"]') ||
                       button.parentElement?.querySelector('input[type="number"]');
    
    if (quantityInput) {
      // If this is a minus button, check minimum before allowing decrease
      if (button.name === 'minus' || button.getAttribute('name') === 'minus') {
        const minQuantity = parseInt(quantityInput.getAttribute('min')) || 
                           parseInt(quantityInput.getAttribute('data-min')) || 1;
        const currentValue = parseInt(quantityInput.value) || 1;
        
        if (currentValue <= minQuantity) {
          event.preventDefault();
          event.stopPropagation();
          quantityInput.value = minQuantity;
          console.log('[Cart Variant Switcher] Prevented decrement below minimum:', minQuantity);
          return;
        }
      }
      
      setTimeout(() => {
        enforceMinimumQuantity(quantityInput);
        handleQuantityChange({ target: quantityInput });
      }, 100);
    } else {
      console.warn('[Cart Variant Switcher] Could not find quantity input from button');
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize the cart variant switcher
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeCartListeners();
        processAllCartItems();
      });
    } else {
      initializeCartListeners();
      processAllCartItems();
    }

    // Re-initialize when cart is updated
    document.addEventListener('cart:updated', initializeCartListeners);
    document.addEventListener('cart:refresh', initializeCartListeners);
    
    // For AJAX cart drawers
    document.addEventListener('cart:open', () => {
      setTimeout(() => {
        initializeCartListeners();
        processAllCartItems();
      }, 300);
    });

    // For Shopify section rendering
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail && event.detail.sectionId && event.detail.sectionId.includes('cart')) {
        console.log('[Cart Variant Switcher] Cart section loaded, re-initializing...');
        setTimeout(() => {
          initializeCartListeners();
          processAllCartItems();
        }, 100);
      }
    });
    
    // MutationObserver to detect cart drawer opening/changes
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      mutations.forEach((mutation) => {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && (node.matches('cart-drawer, .cart-drawer, #CartDrawer') || 
                node.querySelector && node.querySelector('cart-drawer, .cart-drawer, #CartDrawer'))) {
              shouldReinit = true;
            }
          }
        });
        
        // Check for attribute changes on cart drawer (like 'open' or 'hidden')
        if (mutation.type === 'attributes' && mutation.target.matches && 
            mutation.target.matches('cart-drawer, .cart-drawer, #CartDrawer')) {
          shouldReinit = true;
        }
      });
      
      if (shouldReinit) {
        console.log('[Cart Variant Switcher] Cart drawer detected/changed, re-initializing...');
        setTimeout(() => {
          initializeCartListeners();
          processAllCartItems();
        }, 100);
      }
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'hidden', 'class']
    });
  }

  // Start the script
  init();

  // Expose for debugging
  window.CartVariantSwitcher = {
    init: init,
    processAllCartItems: processAllCartItems,
    getVariantIdForQuantity: getVariantIdForQuantity
  };

})();
